import { useCallback, type ComponentType } from "react";
import {
  ArchiveIcon,
  ChartNoAxesColumnIncreasingIcon,
  ArrowLeftIcon,
  KeyboardIcon,
  Settings2Icon,
  UserRoundIcon,
  UsersIcon,
} from "lucide-react";
import { useCanGoBack, useNavigate } from "@tanstack/react-router";

import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "../ui/sidebar";
import { useTranslation } from "react-i18next";
import { isElectron } from "../../env";
import { t } from "~/i18n";
import type { I18nKey } from "~/i18n";
import { useTeamSectionAccess } from "./TeamSettings";

export type SettingsSectionPath =
  | "/settings/general"
  | "/settings/keybindings"
  | "/settings/limits"
  | "/settings/account"
  | "/settings/archived"
  | "/settings/team";

export const SETTINGS_NAV_ITEMS: ReadonlyArray<{
  label: I18nKey;
  to: SettingsSectionPath;
  icon: ComponentType<{ className?: string }>;
}> = [
  { label: "settings/general_pvqyn0", to: "/settings/general", icon: Settings2Icon },
  { label: "settings/keybindings_w12nxu", to: "/settings/keybindings", icon: KeyboardIcon },
  { label: "settings/account_teeon5", to: "/settings/account", icon: UserRoundIcon },
  {
    label: "settings/limits_xn7zw0",
    to: "/settings/limits",
    icon: ChartNoAxesColumnIncreasingIcon,
  },
  { label: "settings/archived_c05kag", to: "/settings/archived", icon: ArchiveIcon },
  { label: "settings/organization_zmd6s2", to: "/settings/team", icon: UsersIcon },
];

export function SettingsSidebarNav({ pathname }: { pathname: string }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const { isMobile, setOpenMobile } = useSidebar();
  const teamSection = useTeamSectionAccess();
  const navItems = SETTINGS_NAV_ITEMS.filter(
    (item) =>
      (item.to !== "/settings/team" || teamSection === "visible") &&
      (item.to !== "/settings/keybindings" || isElectron),
  );
  const handleSectionClick = useCallback(
    (to: SettingsSectionPath) => {
      if (isMobile) {
        setOpenMobile(false);
      }
      void navigate({ to, replace: true });
    },
    [isMobile, navigate, setOpenMobile],
  );
  const handleBackClick = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
    if (canGoBack) {
      window.history.back();
      return;
    }
    void navigate({ to: "/" });
  }, [canGoBack, isMobile, navigate, setOpenMobile]);

  return (
    <>
      <SidebarContent className="overflow-x-hidden">
        <SidebarGroup className="px-2 py-3">
          <SidebarMenu>
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = pathname === item.to;
              return (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    size="sm"
                    isActive={isActive}
                    className={
                      isActive
                        ? "gap-2.5 px-2.5 py-2 text-left text-[13px] font-medium text-foreground"
                        : "gap-2.5 px-2.5 py-2 text-left text-[13px] text-muted-foreground/70 hover:text-foreground/80"
                    }
                    onClick={() => handleSectionClick(item.to)}
                  >
                    <Icon
                      className={
                        isActive
                          ? "size-4 shrink-0 text-foreground"
                          : "size-4 shrink-0 text-muted-foreground/60"
                      }
                    />
                    <span className="truncate">{i18n.t(item.label)}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarSeparator />
      <SidebarFooter className="p-2">
        <SidebarMenu className="min-w-0">
          <SidebarMenuItem>
            <SidebarMenuButton
              size="sm"
              className="gap-2 px-2 py-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={handleBackClick}
            >
              <ArrowLeftIcon className="size-4" />
              <span>{t("common/back_xm988t")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </>
  );
}
